/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Emails Service - SuperAdmin Email Dashboard API
 * ============================================================================
 */

import { get } from '@/services/api/client';
import { ApiResponse, PagedResponse } from '@/services/api/types';

const BASE_URL = '/admin/emails';

export type EmailStatus = 'QUEUED' | 'SENT' | 'FAILED';
export type EmailType = 'PASSWORD_RESET' | 'MFA_CODE' | 'WELCOME' | 'INVITE' | 'EMAIL_VERIFICATION' | 'ACCOUNT_LOCKED' | 'LOGIN_ALERT' | 'OTHER';

export interface EmailLog {
  id: string;
  recipientEmail: string;
  subject: string;
  templateName: string | null;
  emailType: EmailType | null;
  status: EmailStatus;
  orgId: string | null;
  userId: string | null;
  sentAt: string | null;
  errorMessage: string | null;
  locale: string | null;
  createdAt: string;
}

export interface EmailStats {
  last24h: {
    total: number;
    sent: number;
    failed: number;
    queued: number;
    deliveryRate: number;
  };
  last7d: Record<string, number>;
  topTypes: Array<{ type: string; count: number }>;
}

export interface EmailTemplate {
  name: string;
  description: string;
}

export const emailsService = {
  async getStats(): Promise<EmailStats> {
    const response = await get<ApiResponse<EmailStats>>(`${BASE_URL}/stats`);
    return response.data;
  },

  async getEmails(
    page: number = 0,
    size: number = 20,
    params?: { status?: EmailStatus; type?: EmailType; search?: string }
  ): Promise<PagedResponse<EmailLog>> {
    const response = await get<ApiResponse<PagedResponse<EmailLog>>>(BASE_URL, { page, size, ...params });
    return response.data;
  },

  async getEmailTypes(): Promise<string[]> {
    const response = await get<ApiResponse<string[]>>(`${BASE_URL}/types`);
    return response.data;
  },

  async getTemplates(): Promise<EmailTemplate[]> {
    const response = await get<ApiResponse<EmailTemplate[]>>(`${BASE_URL}/templates`);
    return response.data;
  },
};

export default emailsService;
